<script setup lang="ts">
import BasePage from '@/components/pages/BasePage/BasePage.vue';
import BasePageTitle from '@/components/pages/BasePage/BasePageTitle.vue';
import VeeSubmitButton from '@/components/forms/SubmitButton/VeeSubmitButton.vue';
import MedicalSystemForm from './components/MedicalSystemForm.vue';
import { useVeeForm } from '@/components/forms/useVeeForm';
import { api } from '@/api';
import { routenames } from '@/router/routenames';
import { onMounted, computed } from 'vue';
import { type MedicalSystemRtSystemDto } from '@/api/generator';
import { useLoading } from '@/utils/useLoading';
import type { MedicalSystemAddViewProps } from './types';
import BasePageStatus from '@/components/pages/BasePage/BasePageStatus.vue';
import { useRouter } from 'vue-router';
import { useTranslation } from 'i18next-vue';
import { getMedicalSystemSubtitle } from './utils';
import { toCreateMedicalSystemRtSystemDto } from './mappings';
import { useNotificationStore } from '@/stores/notificationStore';
import PtwCancelButton from '@/components/buttons/PtwCancelButton.vue';
import { entityNotFoundErrorHandler, formBackendErrorHandler } from '@/errorHandling';

const props = defineProps<MedicalSystemAddViewProps>();

const router = useRouter();

const { t } = useTranslation();
const notificationStore = useNotificationStore();

const { form } = useVeeForm<MedicalSystemRtSystemDto>({
  schemaName: 'MedicalSystemRtSystemDto',
  mode: 'add',
  confirmationOptions: {
    skipDirtyCheck: 'never', // we decide to never skip "dirty"-dialog, because of initial medical-system selection
  },
});

const { handleSubmit, resetForm, values } = form;

const submit = handleSubmit(async (medicalSystemRtSystemDto) => {
  const { success, result: medicalSystem } =
    await api.medicalSystemsApi.createMedicalSystemRtSystem(
      {
        createMedicalSystemRtSystemDto: toCreateMedicalSystemRtSystemDto(medicalSystemRtSystemDto),
      },
      [formBackendErrorHandler(form)],
    );

  if (success) {
    notificationStore.success(
      t('EntityAdded', { name: medicalSystem.name, iuid: medicalSystem.iuid }),
    );
    await router.push({ name: routenames.medicalSystems.overview });
  }
});

const subtitle = computed(() => getMedicalSystemSubtitle(values));

const { action: loadInitialValues, loading } = useLoading(async () => {
  const { result } = await api.medicalSystemsApi.getMedicalSystemTemplate(props.options, [
    entityNotFoundErrorHandler(router),
  ]);
  if (result) resetForm({ values: { ...result, name: t('Untitled') } });
}, true);

onMounted(loadInitialValues);
</script>

<template>
  <form @submit="submit">
    <BasePage :loading="loading">
      <template #title>
        <BasePageTitle :title="values.name" :subtitle="subtitle" />
      </template>

      <template #status>
        <BasePageStatus />
      </template>

      <template #actions>
        <PtwCancelButton :to="{ name: routenames.medicalSystems.overview }" />
        <VeeSubmitButton class="ml-4" />
      </template>
      <MedicalSystemForm />
    </BasePage>
  </form>
</template>
